import { useState } from "react";
import Form from "react-bootstrap/Form";
import { useNavigate } from "react-router-dom";
import Layout from "../assets/component/Layout";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL

export default function ChangePassword() {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState("");

  const togglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const validationSchema = Yup.object({
    oldPassword: Yup.string().required("Current password is required"),
    newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Passwords must match")
      .required("Confirm password is required"),
  });

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setApiError("");
        const response = await axios.post(`${BASE_URL}change-password`, values, {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        });
        // Only navigate if the response is successful (status 200)
        if (response.data.status === 200) {
          navigate("/home");
        } else {
          setApiError(response.data.message);
        }
      } catch (error) {
        // Set API error to state
        setApiError(
          error.response?.data?.message || "Something went wrong. Try again."
        );
      }
    },

  });

  return (
    <Layout>
      <section className="login-layout">
        <div className="main-login-form">
          <h1>Change Password</h1>
          <p>Fill in your details to change your password.</p>

          <Form onSubmit={formik.handleSubmit}>
            {/* Current Password */}
            <Form.Group className="mb-2">
              <Form.Label>Current Password</Form.Label>
              <div className="adding-eye-icon">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter current password"
                  name="oldPassword"
                  value={formik.values.oldPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.oldPassword &&
                    !!formik.errors.oldPassword
                  }
                />
                <div onClick={togglePassword}>
                  {showPassword ? hideIcon() : showIcon()}
                </div>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.oldPassword}
                </Form.Control.Feedback>
              </div>
            </Form.Group>

            {/* New Password */}
            <Form.Group className="mb-2">
              <Form.Label>New Password</Form.Label>
              <div className="adding-eye-icon">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  name="newPassword"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.newPassword && !!formik.errors.newPassword
                  }
                />
                <div onClick={togglePassword}>
                  {showPassword ? hideIcon() : showIcon()}
                </div>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.newPassword}
                </Form.Control.Feedback>
              </div>
            </Form.Group>

            {/* Confirm Password */}
            <Form.Group className="mb-2">
              <Form.Label>Confirm Password</Form.Label>
              <div className="adding-eye-icon">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  name="confirmPassword"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.confirmPassword &&
                    !!formik.errors.confirmPassword
                  }
                />
                <div onClick={togglePassword}>
                  {showPassword ? hideIcon() : showIcon()}
                </div>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.confirmPassword}
                </Form.Control.Feedback>
              </div>
            </Form.Group>

            {/* API Error Display */}
            {apiError && <p className="text-danger mt-2">{apiError}</p>}

            <button className="mt-4" type="submit">
              Submit
            </button>
          </Form>
        </div>
      </section>
    </Layout>
  );
}

function showIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.439 15.439C20.3636 14.5212 21.0775 13.6091 21.544 12.955C21.848 12.5287 22 12.3155 22 12C22 11.6845 21.848 11.4713 21.544 11.045C20.1779 9.12944 16.6892 5 12 5C11.0922 5 10.2294 5.15476 9.41827 5.41827M6.74742 6.74742C4.73118 8.1072 3.24215 9.94266 2.45604 11.045C2.15201 11.4713 2 11.6845 2 12C2 12.3155 2.15201 12.5287 2.45604 12.955C3.8221 14.8706 7.31078 19 12 19C13.9908 19 15.7651 18.2557 17.2526 17.2526"
        stroke="#545454"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.85786 10C9.32783 10.53 9 11.2623 9 12.0711C9 13.6887 10.3113 15 11.9289 15C12.7377 15 13.47 14.6722 14 14.1421"
        stroke="#545454"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M3 3L21 21"
        stroke="#545454"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function hideIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M21.544 11.045C21.848 11.4713 22 11.6845 22 12C22 12.3155 21.848 12.5287 21.544 12.955C20.1779 14.8706 16.6892 19 12 19C7.31078 19 3.8221 14.8706 2.45604 12.955C2.15201 12.5287 2 12.3155 2 12C2 11.6845 2.15201 11.4713 2.45604 11.045C3.8221 9.12944 7.31078 5 12 5C16.6892 5 20.1779 9.12944 21.544 11.045Z"
        stroke="#545454"
        strokeWidth="1.5"
      />
      <path
        d="M15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12Z"
        stroke="#545454"
        strokeWidth="1.5"
      />
    </svg>
  );
}
